import { z } from 'zod/v4';

/**
 * What a brand saw when it dismissed a supplier-matching warning (DELTA-2763):
 * the number of audits amfori listed for the site, and the companies the
 * matcher considered with how each was classified. A later run's warning
 * whose snapshot differs brings the dismissal back. `linkedMonitoringCount`
 * is `null` on warnings written before it was logged; a `null` side matches
 * any count.
 */
export const AmforiWarningSnapshotSchema = z.object({
  linkedMonitoringCount: z.number().nullable(),
  companies: z.array(
    z.object({
      companyId: z.string(),
      classification: z.string(),
    }),
  ),
});

export type AmforiWarningSnapshot = z.infer<typeof AmforiWarningSnapshotSchema>;

/**
 * Why a dismissed warning came back: the site's amfori audit count moved,
 * and/or the matched companies changed (by company id).
 */
export const AmforiWarningResurfaceReasonSchema = z.object({
  auditCount: z.object({ from: z.number(), to: z.number() }).optional(),
  companies: z
    .object({
      added: z.array(z.string()),
      removed: z.array(z.string()),
      reclassified: z.array(z.string()),
    })
    .optional(),
});

export type AmforiWarningResurfaceReason = z.infer<
  typeof AmforiWarningResurfaceReasonSchema
>;

const NamedCompanySchema = z.object({
  companyId: z.string(),
  name: z.string().nullable(),
});

/** A resurface reason as the report shows it: company ids with their names. */
export const AmforiSyncReportBackReasonSchema = z.object({
  auditCount: z.object({ from: z.number(), to: z.number() }).optional(),
  companies: z
    .object({
      added: z.array(NamedCompanySchema),
      removed: z.array(NamedCompanySchema),
      reclassified: z.array(NamedCompanySchema),
    })
    .optional(),
});

export type AmforiSyncReportBackReason = z.infer<
  typeof AmforiSyncReportBackReasonSchema
>;

/**
 * A report entry's archive state, for the run owner's brand:
 * - `ARCHIVED`: the brand dismissed this warning; `byName` / `at` say who and
 *   when.
 * - `BACK`: the brand had dismissed it, and it came back on its own at `at`
 *   because of `reason`.
 */
export const AmforiSyncReportArchiveSchema = z.object({
  state: z.enum(['ARCHIVED', 'BACK']),
  byName: z.string().nullable(),
  at: z.string().nullable(),
  reason: AmforiSyncReportBackReasonSchema.nullable(),
});

export type AmforiSyncReportArchive = z.infer<
  typeof AmforiSyncReportArchiveSchema
>;

/** Path params of the dismiss / restore routes. */
export const AmforiSyncWarningParamsSchema = z.object({
  syncHistoryId: z.string(),
  entryId: z.string(),
});

export type AmforiSyncWarningParams = z.infer<
  typeof AmforiSyncWarningParamsSchema
>;

/** Response of the dismiss / restore routes: the entry's state after it. */
export const AmforiSyncWarningArchiveResultSchema = z.object({
  archived: z.boolean(),
});

export type AmforiSyncWarningArchiveResult = z.infer<
  typeof AmforiSyncWarningArchiveResultSchema
>;
